import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import Hero from '../components/Hero';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import VersionTable from '../components/VersionTable';
import FaqAccordion from '../components/FaqAccordion';
import ArticleList from '../components/ArticleList';
import { FEATURED_ARTICLES, articlePath } from '../constants/articles';
import { DISCLAIMER_TEXT, HOME_FAQS } from '../constants/content';
import { IMAGES } from '../constants/images';
import { SITE_DOMAIN } from '../constants/site';
import { openZohoChat } from '../utils/openZohoChat';

const TRUST = [
  ['Day & Night', 'Chat whenever you are stuck'],
  ['PC or Mac', 'We know both inside out'],
  ['Old & New', 'From FTM 16 up to FTM 2024'],
  ['Safety First', 'A backup before every fix'],
];

const STRENGTHS = [
  {
    title: 'Private by design',
    text: 'Nothing leaves your computer unless you choose to sync it. For people who want full control over decades of family research, that peace of mind matters.',
  },
  {
    title: 'Grows with your skills',
    text: 'On day one you can add grandparents in minutes. Years later you can be managing thousands of people, detailed citations, and custom reports in the same program.',
  },
  {
    title: 'Plays nicely with online resources',
    text: 'Record hints, Ancestry searches, FamilySearch matching, and two-way syncing mean your desktop tree is never cut off from the wider genealogy world.',
  },
  {
    title: 'Made to be shared',
    text: 'Wall charts for reunions, printed books for grandchildren, or an online copy relatives can browse: sharing your discoveries is built in.',
  },
];

const FTM_2019_TOOLS = [
  {
    title: 'FamilySync',
    text: 'Pairs your desktop tree with one Ancestry tree so edits travel both ways. Do your record hunting online and your tidying at home.',
    link: 'fix-familysync-errors',
  },
  {
    title: 'TreeVault',
    text: 'An optional online vault for copies of your trees. Handy insurance against a dead hard drive and a simple way to set up a new computer.',
    link: 'treevault-cloud-services-guide',
  },
  {
    title: 'Color Coding',
    text: 'Tag each branch of the family with its own color. In a tree with dozens of surnames, it is the quickest way to see who belongs where.',
    link: 'color-coding-features-ftm-2019',
  },
  {
    title: 'Photo Fixes',
    text: 'Lighten dark prints, soften scratches, and crop old portraits without opening a separate photo editor.',
  },
  {
    title: 'FamilySearch Link',
    text: 'Compare your people with the free FamilySearch Family Tree and bring back any facts or sources you are missing.',
    link: 'familysearch-login-in-ftm',
  },
  {
    title: 'Books & Charts',
    text: 'Fan charts, descendant charts, detailed reports, and full family history books through the Family Book Creator plugin.',
    link: 'family-book-creator-ftm-2019',
  },
];

const SESSION_STEPS = [
  ['Say hello', 'Ring us or open chat and describe what you are seeing.'],
  ['We ask questions', 'Which edition, which computer, and what changed recently?'],
  ['We solve it', 'Guided steps on the phone, or a remote session if you prefer.'],
  ['We future-proof', 'You finish with a backup plan so it does not happen again.'],
];

const COMMON_FIXES = [
  ['Setup stalls or will not finish', 'install-family-tree-maker-2024-windows'],
  ['Serial number rejected during activation', 'activate-family-tree-maker-2024'],
  ['Program freezes or closes on launch', 'ftm-2024-not-opening-fix'],
  ['FamilySync errors or endless syncing', 'fix-familysync-errors'],
  ['Pictures replaced by broken-link icons', 'fixing-broken-media-links-ftm-2019'],
  ['Setting up on a replacement computer', 'transfer-ftm-2019-to-new-computer'],
  ['Ancestry sign-in keeps failing', 'sign-in-ancestry-account-ftm'],
  ['Tree running slowly or showing errors', 'avoid-ftm-2019-data-corruption'],
];

const WHATS_NEW = [
  {
    img: IMAGES.whatsNew.cloud,
    title: 'An off-site copy of your work',
    text: 'Pair Family Tree Maker with TreeVault and a copy of your tree sits safely online, away from spills, theft, and failing drives.',
  },
  {
    img: IMAGES.whatsNew.timeline,
    title: 'A life told in order',
    text: 'Each person’s timeline lines up births, moves, marriages, and deaths so gaps in your research stand out immediately.',
  },
  {
    img: IMAGES.whatsNew.search,
    title: 'Records without leaving the program',
    text: 'Search online collections from the Web Search workspace and check each result against your tree before you accept it.',
  },
  {
    img: IMAGES.whatsNew.responsive,
    title: 'Happy on today’s hardware',
    text: 'Built for recent versions of Windows and macOS, sharp high-resolution screens, and Macs with Apple silicon.',
  },
  {
    img: IMAGES.whatsNew.messages,
    title: 'Notes and to-do lists in context',
    text: 'Pin research notes and tasks to the person they are about, so your next step is waiting when you come back.',
  },
  {
    img: IMAGES.whatsNew.video,
    title: 'Every kind of keepsake',
    text: 'Scanned letters, old photos, voice recordings, and home movies can all be attached to the right ancestor.',
  },
];

const EDITIONS = [
  {
    img: IMAGES.products.main,
    title: 'Complete Program',
    text: 'Buying Family Tree Maker for the first time? We can take you from download to your first finished family group.',
  },
  {
    img: IMAGES.products.upgrade,
    title: 'Upgrade Copies',
    text: 'Moving up from an older release? We check that every person, picture, and sync link survives the move.',
  },
  {
    img: IMAGES.products.companion,
    title: 'Learning Companions',
    text: 'Using a printed or digital guide? We fill in the gaps and show you shortcuts the manuals skip.',
  },
  {
    img: IMAGES.products.charts,
    title: 'Chart & Print Add-ons',
    text: 'Planning a wall chart or family book? We help with page setup, printing, and saving to PDF.',
  },
  {
    img: IMAGES.products.deluxe,
    title: 'Gift & Deluxe Sets',
    text: 'Received a bundle with extras? We get each component installed and working together.',
  },
];

export default function HomePage() {
  return (
    <PageShell>
      <h1 className="entry-title">Family Tree Maker Support</h1>

      <Hero />

      <ul className="trust-strip">
        {TRUST.map(([big, small]) => (
          <li key={big}>
            <strong>{big}</strong>
            <span>{small}</span>
          </li>
        ))}
      </ul>

      <CallButton />

      <section className="entry-section">
        <h2>Why Keep Your Family Tree in Software?</h2>
        <p>
          Every family has stories that are one generation away from being forgotten: the
          great-grandmother who crossed an ocean alone, the uncle who changed the spelling of the
          surname, the farm that stayed in the family for a century. Writing those details down in one
          organized place is how they survive.
        </p>
        <p>
          Genealogy software gives that record a structure. Instead of loose notes and shoeboxes of
          photos, you get a searchable database of people, each linked to parents, partners, and
          children, with dates, places, pictures, and the documents that prove them. When relatives ask
          questions, you have answers, and when you find something new, it slots straight into place.
        </p>
        <p>
          <strong>Family Tree Maker</strong> has been one of the go-to desktop programs for this job for
          decades, on both Windows and Mac. It is powerful, but it is still software, and software
          occasionally misbehaves. That is where our independent help center comes in: we sort out
          installs, updates, syncing, missing pictures, and odd error messages by phone, live chat, or
          remote session.
        </p>
        <p>
          Whether you run the popular{' '}
          <Link to="/family-tree-maker-2019">Family Tree Maker 2019</Link>, the current{' '}
          <Link to="/family-tree-maker-2024">Family Tree Maker 2024</Link>, or something much older, we
          can help you get it working.
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Three Easy Ways to Get Help</h2>
        <p>
          The software publisher provides official support for its own products. {SITE_DOMAIN} is a
          separate, independent option for people who want a real person to talk things through. Pick
          whichever way suits you:
        </p>
        <div className="service-grid service-grid--3">
          <div className="service">
            <h3>Chat Online</h3>
            <p>
              Type your question into the chat window and paste in any error message. A technician
              replies in the same window —{' '}
              <button type="button" className="text-link" onClick={openZohoChat}>
                open the chat
              </button>
              .
            </p>
          </div>
          <div className="service">
            <h3>Call Us</h3>
            <p>
              Some problems are easier to explain out loud. Our <strong>toll-free number</strong>{' '}
              connects you with a technician for installs, activation, syncing, or{' '}
              <Link to={articlePath('sign-in-ancestry-account-ftm')}>Ancestry login</Link> trouble.
            </p>
          </div>
          <div className="service">
            <h3>Share Your Screen</h3>
            <p>
              When describing it is not enough, you can let a technician see your screen. Nothing
              happens without your go-ahead, and you can end the session whenever you wish.
            </p>
          </div>
        </div>
      </section>

      <section className="entry-section">
        <h2>What Happens When You Contact Us</h2>
        <div className="steps-grid">
          {SESSION_STEPS.map(([title, text]) => (
            <div key={title} className="service">
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>The Problems We Solve Most Often</h2>
        <p className="text-center">
          If one of these sounds familiar, you are in good company. Each link opens a free walkthrough,
          or you can simply call and let us handle it with you.
        </p>
        <ul className="link-list">
          {COMMON_FIXES.map(([label, slug]) => (
            <li key={slug}>
              <Link to={articlePath(slug)}>{label}</Link>
            </li>
          ))}
        </ul>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Finding Your Way Around Family Tree Maker</h2>
        <p>
          The program is split into workspaces, and each one has a single purpose. Most of your time
          will be spent in <strong>People</strong>, where you can flip between a pedigree chart that
          shows several generations at once and a family view that focuses on one couple and their
          children. It is where you add relatives and edit their facts.
        </p>
        <p>
          <strong>Places</strong> shows where life events happened on a map and helps you standardize
          town and county names. <strong>Media</strong> is your digital shoebox for photos, scans, and
          recordings. <strong>Sources</strong> keeps track of the records behind each fact, which is
          what separates solid research from guesswork.
        </p>
        <p>
          When you are ready to show off your work, <strong>Publish</strong> builds charts, reports, and
          books. And <strong>Web Search</strong> lets you look for records online and compare them with
          the people already in your tree, while the hint leaf flags records that might be a match.
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Highlights of Family Tree Maker 2024</h2>
        <p className="text-center">
          The newest release keeps everything long-time users rely on and smooths out the rough edges.
          These are the improvements people mention to us most:
        </p>
        <div className="img-grid">
          {WHATS_NEW.map((item) => (
            <div key={item.title} className="img-card">
              <img src={item.img} alt="" width={640} height={427} loading="lazy" />
              <div className="img-card__body">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>What Makes Family Tree Maker Worth Using</h2>
        <p>Plenty of genealogy tools exist, yet many researchers stick with this one. Here is why:</p>
        <ul className="tick-list">
          {STRENGTHS.map((item) => (
            <li key={item.title}>
              <strong>{item.title}:</strong> {item.text}
            </li>
          ))}
        </ul>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>A Short History of Family Tree Maker</h2>
        <p className="text-center">
          The program has been around since the late 1980s and has been rebuilt several times along the
          way. The table below shows the main milestones, ending with the current{' '}
          <Link to="/family-tree-maker-2024">FTM 2024</Link>.
        </p>
        <VersionTable />
      </section>

      <section className="entry-section">
        <h2>Family Tree Maker 2019: Still Going Strong</h2>
        <p>
          Years after its release, FTM 2019 remains installed on a huge number of computers. It
          introduced a steadier FamilySync, color coding for family lines, built-in photo repair, and
          TreeVault cloud storage. If it still does everything you need, there is no pressure to
          upgrade, and we are happy to keep it running for you.
        </p>
        <h2 className="h2-sub">Tools FTM 2019 Users Love</h2>
        <div className="feature-list">
          {FTM_2019_TOOLS.map((feature) => (
            <div key={feature.title} className="feature-list__item">
              <h3>{feature.title}</h3>
              <p>
                {feature.text}{' '}
                {feature.link ? <Link to={articlePath(feature.link)}>Full guide ›</Link> : null}
              </p>
            </div>
          ))}
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Getting Family Tree Maker Installed</h2>
        <p>Most installs take ten to fifteen minutes. Choose your type of computer:</p>
        <div className="two-col">
          <div>
            <h3>Windows computers</h3>
            <ol>
              <li>Find the download link in your purchase email, or use the publisher’s site.</li>
              <li>Open the downloaded file and click through the setup screens.</li>
              <li>Type in your serial number when the program asks for it.</li>
              <li>Create a fresh tree or restore a backup of an existing one.</li>
            </ol>
            <p>
              <Link to={articlePath('install-family-tree-maker-2024-windows')}>
                Detailed Windows instructions ›
              </Link>
            </p>
          </div>
          <div>
            <h3>Mac computers</h3>
            <ol>
              <li>Download the macOS version of the installer.</li>
              <li>Move the Family Tree Maker icon into your Applications folder.</li>
              <li>Start the program and enter your serial number.</li>
              <li>If macOS shows a security warning, confirm you want to open it.</li>
            </ol>
            <p>
              <Link to={articlePath('install-family-tree-maker-2024-mac')}>
                Detailed Mac instructions ›
              </Link>
            </p>
          </div>
        </div>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Help for Every Family Tree Maker Package</h2>
        <p className="text-center">
          It does not matter which box, download, or bundle you started with. If it runs Family Tree
          Maker, we can help you set it up and keep it healthy.
        </p>
        <div className="img-grid">
          {EDITIONS.map((item) => (
            <div key={item.title} className="img-card img-card--contain">
              <img src={item.img} alt="" width={520} height={520} loading="lazy" />
              <div className="img-card__body">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="entry-section">
        <h2>Thinking About Family Tree Maker 2024?</h2>
        <p>
          FTM 2024 is the edition the publisher is actively updating. If you are replacing your
          computer, moving to a newer version of Windows or macOS, or running into compatibility
          problems with an older release, it is usually the simplest fix. Your existing trees open in it
          and convert automatically.
        </p>
        <p>
          Start with our <Link to="/family-tree-maker-2024">FTM 2024 help page</Link>, compare your
          computer against the{' '}
          <Link to={articlePath('system-requirements-ftm-2024')}>system requirements</Link>, then work
          through the <Link to={articlePath('upgrade-to-family-tree-maker-2024')}>upgrade guide</Link>.
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Guides Our Visitors Read Most</h2>
        <ArticleList articles={FEATURED_ARTICLES} />
        <p className="text-center">
          <Link to="/blogs" className="btn-outline">
            View the full guide library
          </Link>
        </p>
      </section>

      <HelpBox
        title="Let’s Get Your Tree Back on Track"
        text="Whether it is a stubborn sync, a missing photo, or a program that will not open, a technician is ready to help right now."
      />

      <section className="entry-section">
        <h2>Your Family Story Deserves to Be Told</h2>
        <p>
          Genealogy is part detective work, part storytelling, and part gift to the next generation.
          Family Tree Maker gives you the tools to do all three well. Our job is simply to make sure
          technical problems never get in the way. When you hit a snag, call or chat and we will help
          you pick up right where you left off.
        </p>
      </section>

      <section className="entry-section">
        <h2 className="faq-heading">Quick Answers</h2>
        <FaqAccordion items={HOME_FAQS} />
        <p className="text-center">
          <Link to="/faqs" className="btn-outline">
            Browse all questions
          </Link>
        </p>
      </section>

      <CallButton />

      <section className="entry-section disclaimer-box">
        <h2>Disclaimer</h2>
        <p>{DISCLAIMER_TEXT}</p>
      </section>
    </PageShell>
  );
}
